// src/lib/theme.js

const KEY = 'ui:theme'; // 'light' | 'dark' | 'system'

export function getStoredTheme() {
  try { return localStorage.getItem(KEY) || null; } catch { return null; }
}

export function setThemePreference(theme /* 'light'|'dark'|'system' */) {
  try { localStorage.setItem(KEY, theme); } catch {}
  applyTheme(theme);
}

/** Read the current <html data-theme="..."> if present (used as a fallback) */
function getHtmlAttrTheme() {
  const t = document.documentElement.getAttribute('data-theme');
  return t === 'light' || t === 'dark' ? t : null;
}

/** Last-resort session hint (lowest priority) */
function getSessionTheme() {
  try {
    const s = JSON.parse(localStorage.getItem('session') || 'null');
    const t = s?.theme;
    return t === 'light' || t === 'dark' || t === 'system' ? t : null;
  } catch { return null; }
}

/** If theme === 'system', resolve to actual 'light' | 'dark' */
export function effectiveTheme(theme) {
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return theme === 'dark' ? 'dark' : 'light';
}

/** Decide which theme to apply based on precedence */
export function resolveTheme() {
  // 1) explicit user choice (sticky)
  const stored = getStoredTheme();
  if (stored) return stored;

  // 2) existing HTML attribute (e.g., server-set)
  const htmlAttr = getHtmlAttrTheme();
  if (htmlAttr) return htmlAttr;

  // 3) session hint (optional)
  const sessionTheme = getSessionTheme();
  if (sessionTheme) return sessionTheme;

  // 4) default to system
  return 'system';
}

export function applyTheme(theme /* 'light'|'dark'|'system' */) {
  const eff = effectiveTheme(theme);
  document.documentElement.setAttribute('data-theme', eff);
  // Keep color-scheme consistent for native widgets/scrollbars
  document.documentElement.style.colorScheme = eff;
}

let _mqListenerBound = false;

export function initTheme() {
  // Apply immediately at startup (prevents flash)
  applyTheme(resolveTheme());

  // React to system changes ONLY if user chose 'system'
  if (!_mqListenerBound) {
    try {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const onChange = () => {
        if (getStoredTheme() === 'system') applyTheme('system');
      };
      mq.addEventListener?.('change', onChange);
      mq.addListener?.(onChange); // Safari
      _mqListenerBound = true;
    } catch {}
  }
}

/** Keep theme in sync across tabs and when session changes */
export function watchThemeChanges() {
  const reapply = () => applyTheme(resolveTheme());

  window.addEventListener('storage', (e) => {
    // If another tab changed ui:theme or session, re-evaluate.
    if (e.key === KEY || e.key === 'session') reapply();
  });

  // Your auth layer dispatches 'sessionchange' — re-evaluate but
  // this will NOT override a stored user choice because resolveTheme() prefers ui:theme.
  window.addEventListener('sessionchange', reapply);
  window.addEventListener('focus', reapply);
}